import type { ShoppingCartItem } from "@models/shoppingCart";

/**
 * One line's money: `item.price × quantity`, and nothing else (#432).
 *
 * `item.price` is the line's own vendor offering's price (GET /shoppingcarts maps it from
 * `ShoppingCartItem.VendorSku.Price`), and that offering belongs to the variant's own SKU — so
 * on a variant line it is ALREADY the full variant price. `item.variant.price` is a full price
 * too (the variant SKU's price), never a surcharge: adding it priced variant lines at roughly
 * double. CreateOrderCommand charges exactly this per unit — `OrderProduct.Price =
 * item.VendorSku.Price`, subtotal Σ Price × Quantity — so this figure, and the shipping
 * threshold and card-fee quote it feeds, match what the server charges.
 */
export const lineTotal = (item: ShoppingCartItem): number =>
    item.price * item.quantity;

/**
 * The subtotal for the lines the selected destination can actually receive (#228 / E4-S12).
 *
 * This is a "what you would pay once the restricted lines are gone" figure, NOT a "what you
 * will be charged" one: `CreateOrderCommand` answers 412 naming every restricted product, so
 * the whole order is refused while any of them is still in the cart — nothing is quietly
 * skipped. Any copy rendered next to this number has to say that.
 *
 * Lives here, above `Cart/` and `Checkout/`, because both compute the figure and both feed it
 * onward (checkout's also prices the shipping quote and the card fee). Two copies of this sum
 * is what the branch already had, and they had already started to matter differently.
 */
export const shippableSubtotal = (items: ShoppingCartItem[]): number =>
    items
        .filter((item) => !item.isRestricted)
        .reduce((total, item) => total + lineTotal(item), 0);

/**
 * The units (summed quantities) on the lines `shippableSubtotal` prices — the count that may
 * sit beside that figure. One denominator: "5 Items — $20.00" where the $20.00 is two units
 * of one line and the 5 counts the excluded line's three is a receipt that does not add up.
 */
export const shippableUnitCount = (items: ShoppingCartItem[]): number =>
    items
        .filter((item) => !item.isRestricted)
        .reduce((total, item) => total + item.quantity, 0);

/** How many lines the destination cannot receive — the number the notice quotes. */
export const restrictedLineCount = (items: ShoppingCartItem[]): number =>
    items.filter((item) => item.isRestricted).length;
